/**
 * CoverArtCommentThread — lifted from coverClub/CoverDetailModal.jsx's
 * CommentThread verbatim (same useCoverClub() comment/report plumbing),
 * now living inside Book Details' Cover Art section instead of a separate
 * modal.
 */

import React, { useState } from 'react';
import { Flag } from 'lucide-react';
import { Button } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useCoverClub } from '@/components/coverClub/context/CoverClubContext';
import { formatRelativeTime } from '@/components/coverClub/utils/coverHelpers';
import { EMPTY_STATE_MESSAGES } from '@/components/coverClub/constants';

export default function CoverArtCommentThread({ bookId }) {
  const { getComments, addComment, reportComment } = useCoverClub();
  const [text, setText] = useState('');
  const comments = getComments(bookId);

  const submit = () => {
    if (!text.trim()) return;
    addComment(bookId, text);
    setText('');
  };

  return (
    <div className="mt-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-subtle">What caught your eye?</p>

      {comments.length === 0 ? (
        <p className="mt-3 text-sm text-subtle">{EMPTY_STATE_MESSAGES.noComments.subheading}</p>
      ) : (
        <ul className="mt-3 space-y-3">
          {comments.map((c) => (
            <li key={c.id} className="group flex items-start justify-between gap-2 rounded-xl bg-cream/60 px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-ink">
                  @{c.author}{c.mine && <span className="ml-1 font-normal text-subtle">(you)</span>}
                </p>
                <p className="mt-0.5 text-sm text-ink/85">{c.text}</p>
                <p className="mt-1 text-[10px] text-subtle">{formatRelativeTime(c.createdAt)}</p>
              </div>
              {!c.mine && (
                <button
                  type="button"
                  onClick={() => { reportComment(c.id); toast('Comment reported — our team will review it.'); }}
                  aria-label="Report comment"
                  className="shrink-0 rounded-full p-1.5 text-subtle opacity-0 transition-opacity hover:text-coral group-hover:opacity-100"
                >
                  <Flag className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex items-center gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="What drew you to this cover?"
          data-testid="cover-comment-input"
          className="w-full rounded-full border border-line bg-cream px-4 py-2 text-sm text-ink outline-none focus:border-coral"
        />
        <Button variant="primary" icon={false} onClick={submit} disabled={!text.trim()} className="shrink-0 px-4 py-2 text-xs" data-testid="cover-comment-submit">
          Post
        </Button>
      </div>
    </div>
  );
}
